import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, Bot, RefreshCw, Link2, Loader2 } from 'lucide-react';
import { useAdminLang } from '../../hooks/useAdminLang';

// Admin Security — AI cyberdefense agent + Green IA data-protection explainer
export const CyberdefensePanel = () => {
  const { at } = useAdminLang();
  const [status, setStatus] = useState(null);
  const [report, setReport] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [integrity, setIntegrity] = useState(null);

  const load = () => axios.get('/api/admin/cyberdefense/status').then(r => setStatus(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const analyze = async () => {
    setAnalyzing(true);
    try {
      const r = await axios.post('/api/admin/cyberdefense/analyze');
      setReport(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Agent indisponible');
    } finally {
      setAnalyzing(false);
    }
  };

  const checkIntegrity = async () => {
    try {
      const r = await axios.get('/api/admin/cyberdefense/greenia-integrity');
      setIntegrity(r.data);
      toast[r.data.intact ? 'success' : 'error'](r.data.intact ? (at('cyberChainOk') || 'Registre Green IA intact ✓') : 'ALERTE : chaîne altérée !');
    } catch { toast.error('Erreur'); }
  };

  return (
    <div className="mt-4 p-4 rounded-2xl bg-gradient-to-br from-cyan-500/10 to-blue-900/10 border border-cyan-500/30" data-testid="cyberdefense-panel">
      <div className="flex items-center gap-2 mb-3">
        <Bot className="w-4 h-4 text-cyan-300" />
        <h4 className="font-bold text-cyan-200 text-sm">{at('cyberTitle') || 'Agent IA Cyberdéfense'}</h4>
        <button onClick={load} className="ml-auto p-1.5 rounded-full bg-white/5 hover:bg-white/10" data-testid="cyber-refresh"><RefreshCw className="w-3.5 h-3.5 text-gray-400" /></button>
      </div>

      {status && (
        <>
          {/* Metrics */}
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="p-2 rounded-xl bg-black/30 text-center"><p className="text-lg font-bold text-white">{status.metrics.green_ia_protected_people}</p><p className="text-[9px] text-gray-500">{at('cyberProtected') || 'Personnes protégées Green IA'}</p></div>
            <div className="p-2 rounded-xl bg-black/30 text-center"><p className="text-lg font-bold text-white">{status.metrics.green_ia_audit_entries}</p><p className="text-[9px] text-gray-500">{at('cyberAudit') || 'Entrées audit chaînées'}</p></div>
            <div className="p-2 rounded-xl bg-black/30 text-center"><p className="text-lg font-bold text-white">{status.metrics.failed_logins_7d}</p><p className="text-[9px] text-gray-500">{at('cyberFailed') || 'Connexions échouées (7j)'}</p></div>
          </div>

          {/* Posture checks */}
          <div className="space-y-1 mb-3" data-testid="cyber-checks">
            {status.checks.map(c => (
              <p key={c.id} className="text-[11px] flex items-start gap-1.5">
                <span className={c.ok ? 'text-emerald-400' : 'text-red-400'}>{c.ok ? '✓' : '✗'}</span>
                <span className="text-gray-300">{c.label}</span>
              </p>
            ))}
          </div>

          {/* Green IA data protection explainer */}
          <div className="mb-3 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/25" data-testid="cyber-greenia-explainer">
            <p className="text-[11px] font-bold text-emerald-300 mb-1.5 flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" /> {at('cyberGreenTitle') || 'Comment les données Green IA sont protégées'}</p>
            {status.green_ia_data_protection.map((line, i) => (
              <p key={i} className="text-[10px] text-emerald-100/70 leading-snug mb-1">• {line}</p>
            ))}
          </div>
        </>
      )}

      <div className="flex gap-2">
        <button onClick={analyze} disabled={analyzing} className="flex-1 py-2.5 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 text-sm font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2" data-testid="cyber-analyze-btn">
          {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />} {at('cyberAnalyze') || 'Lancer une analyse IA'}
        </button>
        <button onClick={checkIntegrity} className="px-4 py-2.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-sm text-emerald-200 font-semibold flex items-center gap-1.5" data-testid="cyber-integrity-btn">
          <Link2 className="w-3.5 h-3.5" /> {at('cyberVerify') || 'Vérifier le registre'}
        </button>
      </div>

      {integrity && (
        <p className={`mt-2 text-[11px] ${integrity.intact ? 'text-emerald-300' : 'text-red-300'}`} data-testid="cyber-integrity-result">
          {integrity.intact ? `✓ ${at('cyberChainOk') || 'Chaîne d\'audit intacte'} (${integrity.entries} ${at('cyberEntries') || 'entrées scellées'})` : `✗ Altération détectée à l'entrée ${integrity.broken_at_seq}`}
        </p>
      )}

      {report && (
        <div className="mt-3 p-3 rounded-xl bg-black/40 border border-cyan-500/25" data-testid="cyber-report">
          <p className="text-[11px] font-bold text-cyan-300 mb-1.5">{at('cyberReportTitle') || 'Rapport de l\'agent IA'} · {new Date(report.created_at).toLocaleString()}</p>
          <p className="text-[11px] text-gray-300 whitespace-pre-wrap leading-relaxed">{report.report}</p>
        </div>
      )}
    </div>
  );
};

export default CyberdefensePanel;
